import type { Organization, Site, invoicee } from 'interfaces';
import { CanonicalRole, normalizeRole, normalizeSite } from './shared';

const makeOrganizationSeed = (organization: Organization): Organization =>
  organization;

const makeRoleSeed = (
  role: Pick<CanonicalRole, 'id' | 'name' | 'organization' | 'organizationID'> &
    Partial<CanonicalRole>
): CanonicalRole => ({
  permissions: [],
  ...role,
});

const siteDefaults: Omit<
  Site,
  | 'id'
  | 'name'
  | 'address'
  | 'city'
  | 'province'
  | 'region'
  | 'status'
  | 'description'
  | 'proFees'
  | 'techFees'
  | 'organization'
  | 'organizationID'
  | 'siteNumber'
> = {
  fax: '+12045555555',
  email: 'test@example.com',
  phone: '+12045555555',
  postalCode: 'R1R1R1',
  overhead: 15.0,
  invoicee: null,
  invoiceRate: null,
  apiID: '',
  apiSecret: '',
  apiKey: '',
  autoRemit: false,
  cheque: false,
  creditCard: false,
  etransfer: false,
  requiresTariffIcd9: true,
  creditCardPhoneNumber: null,
  etransferEmail: null,
  etransferPasswordRequirement: null,
  timezone: 'America/Winnipeg',
  allowUploads: true,
  settings: { defaultPayorID: null },
};

const makeSiteSeed = (
  site: Pick<
    Site,
    | 'id'
    | 'name'
    | 'address'
    | 'city'
    | 'province'
    | 'region'
    | 'status'
    | 'description'
    | 'proFees'
    | 'techFees'
    | 'organization'
    | 'organizationID'
    | 'siteNumber'
  > &
    Partial<Site>
): Site => ({
  ...siteDefaults,
  hospitalCode: '',
  ...site,
});

export const organizationRefs = {
  winnipegJets: makeOrganizationSeed({
    id: '35bc3fb7-adb0-4ec8-84c0-22d7ed0b1ea3',
    name: 'Winnipeg Jets',
    address: '300 Portage Ave',
    city: 'Winnipeg',
    province: 'MB',
  }),
  safeway: makeOrganizationSeed({
    id: '229dcbb6-a0e7-4287-a373-385d251b6c5e',
    name: 'Safeway',
    address: 'New Street name',
    city: 'Winnipeg',
    province: 'MB',
  }),
  integrationTest: makeOrganizationSeed({
    id: '8f2decd9-6e32-4746-864b-9c9ba5367775',
    name: 'Integration Test Organization ',
    address: '123 Fake Street',
    city: 'Winnipeg',
    province: 'MB',
  }),
  legacyTestSite: makeOrganizationSeed({
    id: 'ff88e4f8-3b0b-49c7-aa8e-4d5095e1db3b',
    name: 'Test Site Organization',
    address: '123 Fake Street',
    city: 'Winnipeg',
    province: 'MB',
  }),
  jamesHouse: makeOrganizationSeed({
    id: '53ff0a7e-ab2b-4cc5-9cf8-ddf5eba85997',
    name: 'James House Organization',
    address: '1 Hipster Ave',
    city: 'Winnipeg',
    province: 'MB',
  }),
} satisfies Record<string, Organization>;

export const roleRefs = {
  integrationTestUser: makeRoleSeed({
    id: 'c159e7ff-3c56-495e-be74-d38ef0cfcf93',
    name: 'Integration Test User',
    organizationID: organizationRefs.integrationTest.id,
    organization: organizationRefs.integrationTest,
  }),
  winnipegJetsUser: makeRoleSeed({
    id: '23799f4a-54cc-4e64-b09f-0418369e2074',
    name: 'Winnipeg Jets User',
    organizationID: organizationRefs.winnipegJets.id,
    organization: organizationRefs.winnipegJets,
  }),
  winnipegJetsAdmin: makeRoleSeed({
    id: 'b5c7b069-63c2-4c38-b289-cbe94afe3a40',
    name: 'Winnipeg Jets Organization Admin',
    organizationID: organizationRefs.winnipegJets.id,
    organization: organizationRefs.winnipegJets,
  }),
  orgAdmin: makeRoleSeed({
    id: 'b997d104-f5ae-4341-b629-1f11586f7837',
    name: 'Org Admin',
    organizationID: organizationRefs.safeway.id,
    organization: organizationRefs.safeway,
  }),
  safewayUser: makeRoleSeed({
    id: '77e8fad9-f147-4eaa-b7e7-070920b4214c',
    name: 'Safeway User',
    organizationID: organizationRefs.safeway.id,
    organization: organizationRefs.safeway,
  }),
} satisfies Record<string, CanonicalRole>;

export const siteRefs = {
  canadaLifeCentre: makeSiteSeed({
    id: 'bf0f1119-34e1-4c9d-b6be-062fdff3fde5',
    name: 'Canada Life Centre',
    address: '300 Portage Ave',
    region: 'Southern',
    status: 'Active',
    description: 'Go Jets Go ',
    proFees: true,
    techFees: true,
    siteNumber: '123AA',
    hospitalCode: '007',
    city: 'Winnipeg',
    province: 'MB',
    organization: organizationRefs.winnipegJets,
    organizationID: organizationRefs.winnipegJets.id,
  }),
  legacyTestSiteNorth: makeSiteSeed({
    id: 'e39e5790-e3ca-40cd-a6ea-22aa08785236',
    name: 'Test Site',
    address: '123 Fake Street',
    region: 'North',
    status: 'Active',
    description: 'This is a description.',
    proFees: false,
    techFees: false,
    siteNumber: '007JB',
    hospitalCode: '007',
    city: 'Winnipeg',
    province: 'MB',
    organization: organizationRefs.legacyTestSite,
    organizationID: organizationRefs.legacyTestSite.id,
  }),
  jamesHouse: makeSiteSeed({
    id: '8ae50a7f-dd28-4aef-8e50-95e3698b42e1',
    name: 'James House',
    address: '1 Hipster Ave',
    fax: '2047974401',
    region: 'Winnipeg',
    status: 'Active',
    description: 'Lil hipster boi in a hipster lil world',
    proFees: false,
    techFees: true,
    siteNumber: '111YM',
    hospitalCode: '007',
    city: 'Winnipeg',
    province: 'MB',
    organization: organizationRefs.jamesHouse,
    organizationID: organizationRefs.jamesHouse.id,
  }),
  integrationTestSite: makeSiteSeed({
    id: '5254bf3d-b65f-46ef-9818-362a687be213',
    name: 'Test Site',
    address: '123 Fake Street',
    region: 'Winnipeg',
    status: 'Active',
    description: 'fake desc',
    proFees: true,
    techFees: true,
    siteNumber: '',
    hospitalCode: '',
    city: 'Winnipeg',
    province: 'MB',
    organization: organizationRefs.integrationTest,
    organizationID: organizationRefs.integrationTest.id,
  }),
  integrationVirtualSite: makeSiteSeed({
    id: 'fe578cc0-3db6-43b8-aca3-b91814aab2db',
    name: 'Integration Test Site',
    address: '123 Fake Street',
    region: 'Winnipeg',
    status: 'Active',
    description: 'Integration Test Site ',
    proFees: false,
    techFees: false,
    siteNumber: '',
    hospitalCode: '',
    city: 'Winnipeg',
    province: 'MB',
    organization: organizationRefs.integrationTest,
    organizationID: organizationRefs.integrationTest.id,
  }),
  qdocVirtualHealthcare: makeSiteSeed({
    id: 'cb2b0bc7-8e08-4c16-8792-49a9a4dbbe3e',
    name: 'QDoc Virtual Healthcare',
    address: '213 Notre Dame',
    fax: '2047974401',
    region: 'Winnipeg',
    status: 'Active',
    description: 'Virtual Healthcare Headquarters',
    proFees: false,
    techFees: true,
    siteNumber: '38H',
    hospitalCode: '007',
    city: 'Winnipeg',
    province: 'MB',
    invoicee: 'site' as invoicee,
    invoiceRate: 7.53,
    organization: organizationRefs.safeway,
    organizationID: organizationRefs.safeway.id,
  }),
} satisfies Record<string, Site>;

export const canonicalSites: Site[] = [
  siteRefs.canadaLifeCentre,
  siteRefs.legacyTestSiteNorth,
  siteRefs.jamesHouse,
  siteRefs.integrationTestSite,
  siteRefs.integrationVirtualSite,
  siteRefs.qdocVirtualHealthcare,
].map(normalizeSite);

export const canonicalOrganizations: Organization[] = [
  organizationRefs.winnipegJets,
  organizationRefs.safeway,
  organizationRefs.integrationTest,
  organizationRefs.legacyTestSite,
  organizationRefs.jamesHouse,
].map((organization) => ({
  ...organization,
  sites: canonicalSites.filter(
    (site) => site.organizationID === organization.id
  ),
}));

export const canonicalRoles: CanonicalRole[] = [
  roleRefs.integrationTestUser,
  roleRefs.winnipegJetsUser,
  roleRefs.winnipegJetsAdmin,
  roleRefs.orgAdmin,
  roleRefs.safewayUser,
].map(normalizeRole);

export const canonicalSiteByID = new Map(
  canonicalSites.map((site) => [site.id, site])
);
